// src/pages/reports/HSNSaleSummaryNew.jsx
import React, { useEffect, useMemo, useState, useDeferredValue } from "react";
import { Row, Col, Card, CardBody, Input, Button, Spinner } from "reactstrap";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { ImFileExcel, ImFilePdf } from "react-icons/im";
import { FaChevronRight, FaChevronDown } from "react-icons/fa";

import { _Get } from "../import/ApiEndPoint/ApiCalling";
import { view_create_order_history_database } from "../import/ApiEndPoint/Api";
import { CheckPermission } from "../import/CheckPermission";
import SuperAdminUI from "../import/SuperAdminUI";

/* =========================
   Inline CSS
   ========================= */
const injectOnce = () => {
  if (document.getElementById("hsn-inline-css-v6")) return;
  const s = document.createElement("style");
  s.id = "hsn-inline-css-v6";
  s.innerHTML = `
    :root{
      --bg-soft-1:#f7fbff;
      --bg-soft-2:#f8fff9;
      --bg-soft-3:#fff8fb;
      --ink:#0f172a;
      --muted:#6b7280;
      --danger-bg:#fee2e2;
      --danger-bd:#ef4444;
      --danger-tx:#991b1b;
      --ok-bg:#ecfdf5;
      --ok-bd:#10b981;
      --ok-tx:#065f46;
    }

    body .hsn-soft-bg{
      background: radial-gradient(1200px 600px at 10% -10%, var(--bg-soft-1), transparent 60%),
                  radial-gradient(1200px 600px at 90% 0%, var(--bg-soft-2), transparent 60%),
                  radial-gradient(1000px 500px at 50% 100%, var(--bg-soft-3), transparent 60%);
      padding-bottom: 8px;
      border-radius: 16px;
    }

    .hsnHdrCard{
      border:1px solid #e5e7eb;
      background:#ffffffcc;
      border-radius:16px;
      padding:10px 12px;
      backdrop-filter: blur(4px);
      box-shadow: 0 6px 18px rgba(15,23,42,.05);
    }

    .hsnHdrRow{
      display:flex;
      align-items:flex-end;
      gap:10px;
      flex-wrap:nowrap;
      overflow-x:auto;
      -webkit-overflow-scrolling: touch;
      padding-bottom:2px;
    }
    .hsnHdrRow::-webkit-scrollbar{height:6px}
    .hsnHdrRow::-webkit-scrollbar-thumb{background:#e5e7eb;border-radius:999px}

    .hsnField{
      display:flex;
      flex-direction:column;
      gap:4px;
      min-width:170px;
    }
    .hsnField.small{min-width:140px}
    .hsnField.wide{min-width:220px}

    .hsnLbl{
      font-size:11px;
      color:#64748b;
      font-weight:800;
      letter-spacing:.02em;
      line-height:1;
      margin:0;
      text-transform:uppercase;
      white-space:nowrap;
    }

    .hsnCtl{
      height:34px !important;
      border-radius:12px !important;
      border:1px solid #e5e7eb !important;
      padding:6px 10px !important;
      background:#fff;
    }

    .hsnIconBtn{
      height:34px;
      width:42px;
      border-radius:12px;
      display:inline-flex;
      align-items:center;
      justify-content:center;
      padding:0;
    }
    .hsnIconBtn svg{font-size:18px}

    .hsnSpacer{flex:1; min-width:16px}

    .hsnMeta{
      padding-bottom:6px;
      font-weight:900;
      color:var(--ink);
      white-space:nowrap;
    }
    .hsnMetaMuted{
      padding-bottom:6px;
      color:var(--muted);
      font-weight:800;
      white-space:nowrap;
    }

    .list-card{border:1px solid #e5e7eb;border-radius:14px;padding:8px;background:#fff}
    .section-title{font-weight:800;font-size:16px;margin:10px 0 8px 0;display:flex;align-items:center;gap:8px;color:var(--ink)}
    .section-title .pill{background:#eef2ff;color:#1e293b;font-weight:800;padding:2px 8px;border-radius:999px;font-size:12px}

    .pa-table-wrap{width:100%;overflow:auto;border:1px solid #e5e7eb;border-radius:14px;background:#fff}
    .pa-table{width:100%;border-collapse:collapse;min-width:1320px}
    .pa-table thead th{
      background:linear-gradient(180deg,#f7fafc,#f1f5f9);
      color:#0f172a;font-weight:800;text-transform:uppercase;font-size:12px;letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb;padding:8px;position:sticky;top:0;z-index:1;text-align:center;white-space:nowrap
    }
    .pa-table tbody td{padding:8px;border-bottom:1px solid #f1f5f9;vertical-align:middle;color:#0f172a;font-size:14px;text-align:center}
    .pa-table tbody tr:hover{background:#f9fbff}

    .sum-bar{border:1px solid #e5e7eb;border-radius:14px;background:#ffffffcc;padding:12px;margin:8px 0}
    .sum-grid-6{display:grid;grid-template-columns:repeat(6,minmax(120px,1fr));gap:10px}
    .sum-box{border:1px solid #e5e7eb;border-radius:12px;padding:10px;background:#fafafa}
    .sum-k{font-size:12px;color:var(--muted)}
    .sum-v{font-weight:900;letter-spacing:.02em}

    .muted{color:var(--muted)}
    .error-bar{border:1px solid #ef4444;background:#fee2e2;color:#991b1b;border-radius:12px;padding:10px 12px;font-weight:900;text-transform:uppercase;letter-spacing:.04em;margin:8px 0}
    .ok-bar{border:1px solid #10b981;background:#ecfdf5;color:#065f46;border-radius:12px;padding:10px 12px;font-weight:900;text-transform:uppercase;letter-spacing:.04em;margin:8px 0}

    .hsn-month-row{
      background:#eef6ff !important;
      font-weight:900;
      cursor:pointer;
    }
    .hsn-month-row:hover{
      background:#e4efff !important;
    }
    .hsn-month-cell{
      display:flex;
      align-items:center;
      gap:8px;
      justify-content:flex-start;
    }
    .hsn-month-muted{
      color:#64748b;
      font-weight:700;
    }
    .hsn-final-row td{
      font-weight:800;
      background:#fafafa;
    }

    .hsn-mismatch{
      background:var(--danger-bg) !important;
      color:var(--danger-tx) !important;
      box-shadow: inset 0 0 0 1px var(--danger-bd);
      font-weight:900 !important;
    }
    .hsn-match{
      background:var(--ok-bg) !important;
      color:var(--ok-tx) !important;
      box-shadow: inset 0 0 0 1px var(--ok-bd);
      font-weight:900 !important;
    }

    .reco-table{width:100%;border-collapse:collapse;min-width:1200px}
    .reco-table th,.reco-table td{
      border:1px solid #e5e7eb;
      padding:8px;
      text-align:center;
      white-space:nowrap;
    }
    .reco-table th{
      background:#f4f7f6;
      font-size:12px;
      text-transform:uppercase;
    }
  `;
  document.head.appendChild(s);
};

/* =========================
   Helpers
   ========================= */
const EPS = 0.05;
const N = (v) => (isNaN(Number(v)) ? 0 : Number(v));
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const num = (n) => (isNaN(Number(n)) ? "0.00" : Number(n).toFixed(2));
const toStr = (v) => (v == null ? "" : String(v));
const sameNum = (a, b) => Math.abs(N(a) - N(b)) <= EPS;
const mismatchClass = (a, b) => (sameNum(a, b) ? "hsn-match" : "hsn-mismatch");

const getUserData = () => JSON.parse(localStorage.getItem("userData") || "{}");

const buildSelectedDatabase = (baseDatabase = "") => {
  const financialYearSelected = localStorage.getItem("financialYearSelected");
  const db = String(baseDatabase || "").trim();

  if (!db) return "";
  if (!financialYearSelected) return db;
  if (/-\d{4}-\d{2}$/.test(db)) return db;

  return `${db}-${financialYearSelected}`;
};

const isCompletedInvoice = (order = {}) =>
  String(order?.status || "")
    .trim()
    .toLowerCase() === "completed";

const getSalesOrderListFromResponse = (res = {}) => {
  const b2bData = Array.isArray(res?.b2bData) ? res.b2bData : [];
  const b2csData = Array.isArray(res?.b2csData) ? res.b2csData : [];
  const b2clData = Array.isArray(res?.b2clData) ? res.b2clData : [];

  const dataB2B = Array.isArray(res?.data?.b2bData) ? res.data.b2bData : [];
  const dataB2CS = Array.isArray(res?.data?.b2csData) ? res.data.b2csData : [];
  const dataB2CL = Array.isArray(res?.data?.b2clData) ? res.data.b2clData : [];

  const combinedBuckets = [
    ...b2bData,
    ...b2csData,
    ...b2clData,
    ...dataB2B,
    ...dataB2CS,
    ...dataB2CL,
  ];

  if (combinedBuckets.length) return combinedBuckets;
  if (Array.isArray(res?.orderHistory)) return res.orderHistory;
  if (Array.isArray(res?.OrderHistory)) return res.OrderHistory;
  if (Array.isArray(res?.orders)) return res.orders;
  if (Array.isArray(res?.Orders)) return res.Orders;
  if (Array.isArray(res?.data?.orderHistory)) return res.data.orderHistory;
  return [];
};

const splitInvoicesIntoGstBuckets = (list = []) => {
  const b2bData = [];
  const b2csData = [];
  const b2clData = [];

  (Array.isArray(list) ? list : []).forEach((invoice) => {
    const registrationType = String(
      invoice?.partyId?.registrationType || invoice?.registrationType || "",
    )
      .trim()
      .toLowerCase();

    const grandTotal = N(
      invoice?.grandTotal ??
        invoice?.GrandTotal ??
        invoice?.grand_total ??
        invoice?.totalAmount ??
        invoice?.TotalAmount,
    );

    if (registrationType === "regular") {
      b2bData.push(invoice);
      return;
    }

    if (
      registrationType === "unregister" ||
      registrationType === "unregistered" ||
      registrationType === "unregistered customer"
    ) {
      if (grandTotal <= 50000) b2csData.push(invoice);
      else b2clData.push(invoice);
    }
  });

  return { b2bData, b2csData, b2clData };
};

const getFYStartYear = (d = new Date()) => {
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = dt.getMonth();
  return m >= 3 ? y : y - 1;
};

const FY_MONTHS = [
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
];

const FY_QUARTERS = [
  { value: "Q1", label: "Q1 (Apr-Jun)", months: [4, 5, 6] },
  { value: "Q2", label: "Q2 (Jul-Sep)", months: [7, 8, 9] },
  { value: "Q3", label: "Q3 (Oct-Dec)", months: [10, 11, 12] },
  { value: "Q4", label: "Q4 (Jan-Mar)", months: [1, 2, 3] },
];

const FY_HALVES = [
  { value: "H1", label: "H1 (Apr-Sep)", months: [4, 5, 6, 7, 8, 9] },
  { value: "H2", label: "H2 (Oct-Mar)", months: [10, 11, 12, 1, 2, 3] },
];

const FY_MONTH_ORDER = [
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
  "January",
  "February",
  "March",
];

const fyLabel = (startYear) => {
  const endYY = String((startYear + 1) % 100).padStart(2, "0");
  return `FY ${startYear}-${endYY}`;
};

function inFinancialPeriod(
  dateValue,
  fyStartYear,
  filterType,
  month,
  quarter,
  half,
) {
  const d = dateValue ? new Date(dateValue) : null;
  if (!d || Number.isNaN(d.getTime())) return false;

  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const rowFY = m >= 4 ? y : y - 1;

  if (rowFY !== Number(fyStartYear)) return false;

  if (filterType === "month") return m === Number(month);

  if (filterType === "quarter") {
    const q = FY_QUARTERS.find((x) => x.value === quarter);
    return q ? q.months.includes(m) : true;
  }

  if (filterType === "half") {
    const h = FY_HALVES.find((x) => x.value === half);
    return h ? h.months.includes(m) : true;
  }

  return true;
}

function formatCurrentRangeLabel(
  filterType,
  fyStartYear,
  month,
  quarter,
  half,
) {
  if (filterType === "month") {
    const m = FY_MONTHS.find((x) => x.value === Number(month));
    return `${fyLabel(Number(fyStartYear))} / ${m?.label || ""}`;
  }
  if (filterType === "quarter") {
    const q = FY_QUARTERS.find((x) => x.value === quarter);
    return `${fyLabel(Number(fyStartYear))} / ${q?.label || ""}`;
  }
  if (filterType === "half") {
    const h = FY_HALVES.find((x) => x.value === half);
    return `${fyLabel(Number(fyStartYear))} / ${h?.label || ""}`;
  }
  return fyLabel(Number(fyStartYear));
}

function getMonthNameFromDate(d) {
  return new Date(d).toLocaleString("en-IN", { month: "long" });
}

function getMonthSortIndex(monthName) {
  const i = FY_MONTH_ORDER.indexOf(monthName);
  return i >= 0 ? i : 999;
}

function getMonthLabelFromDate(dateValue, fyStartYear) {
  const d = new Date(dateValue);
  const monthName = getMonthNameFromDate(d);
  const month = d.getMonth() + 1;
  const year = month >= 4 ? fyStartYear : fyStartYear + 1;
  return `${monthName} ${year}`;
}

function invoiceKey(order) {
  return String(order?._id || order?.invoiceId || "");
}

function invoiceExactTaxable(order) {
  const grand = N(
    order?.grandTotal ??
      order?.GrandTotal ??
      order?.grand_total ??
      order?.totalAmount ??
      order?.TotalAmount,
  );
  const cgst = N(order?.cgstTotal);
  const sgst = N(order?.sgstTotal);
  const igst = N(order?.igstTotal);
  const roundOff = N(
    order?.roundOff ??
      order?.roundoff ??
      order?.RoundOff ??
      order?.round_off ??
      order?.Round_Off,
  );

  const direct = N(
    order?.amount ??
      order?.Amount ??
      order?.taxableAmount ??
      order?.basicTotal ??
      order?.BasicTotal,
  );

  if (direct > 0) return round2(direct);
  return round2(grand - cgst - sgst - igst - roundOff);
}

function invoiceExactRoundOff(order) {
  return round2(
    N(
      order?.roundOff ??
        order?.roundoff ??
        order?.RoundOff ??
        order?.round_off ??
        order?.Round_Off,
    ),
  );
}

function invoiceExactGrand(order) {
  return round2(
    N(
      order?.grandTotal ??
        order?.GrandTotal ??
        order?.grand_total ??
        order?.totalAmount ??
        order?.TotalAmount,
    ),
  );
}

/* ===== HSN item getters ===== */
function getHSN(it) {
  const h =
    it?.HSN_Code ??
    it?.hsn ??
    it?.hsnCode ??
    it?.productId?.HSN_Code ??
    it?.productId?.hsn ??
    "";
  return String(h || "").trim();
}
function getDesc(it) {
  return (
    it?.Product_Desc ??
    it?.productName ??
    it?.productId?.Product_Name ??
    it?.productId?.Product_Title ??
    it?.productId?.name ??
    ""
  );
}
function getUQC(it) {
  return it?.uqc ?? it?.unit ?? it?.primaryUnit ?? it?.productId?.unit ?? "";
}
function baseRate(it) {
  const r =
    it?.gstPercentage ??
    it?.productId?.GSTRate ??
    N(it?.cgstRate) + N(it?.sgstRate) + N(it?.igstRate);
  return N(r);
}

const RATE_CHANGE_DT = new Date("2025-09-22T00:00:00+05:30");
const isHSN480640 = (hsn) =>
  hsn && (hsn === "480640" || hsn.startsWith("480640"));
const matchSpecialThickness = (txt) => {
  const s = String(txt || "").toLowerCase();
  return (
    /(^|[^\d])6\.1([^\d]|$)/.test(s) ||
    /(^|[^\d])6\.5([^\d]|$)/.test(s) ||
    /(^|[^\d])7\.3([^\d]|$)/.test(s)
  );
};
function effectiveRate(orderDate, item) {
  const hsn = getHSN(item);
  const desc = getDesc(item);
  if (isHSN480640(hsn) && matchSpecialThickness(desc)) {
    const d = new Date(orderDate);
    if (isFinite(d)) return d >= RATE_CHANGE_DT ? 18 : 12;
    return baseRate(item) || 18;
  }
  return baseRate(item);
}

function getOverallCharges(order) {
  let c = N(order?.overAllCharges) || N(order?.otherCharges) || 0;
  const arr = Array.isArray(order?.discountDetails)
    ? order.discountDetails
    : [];
  for (const d of arr) {
    const t = String(d?.type || d?.title || d?.name || "").toLowerCase();
    const isCharge =
      t.includes("charge") ||
      t.includes("cartage") ||
      t.includes("coolie") ||
      t.includes("freight") ||
      t.includes("packing") ||
      t.includes("delivery");
    if (isCharge)
      c += N(d?.amount ?? d?.discountedAmount ?? d?.discountedValue);
  }
  return round2(c);
}

function provisionalItemTaxable(it, rate) {
  const qty = N(it?.qty);
  const price = N(it?.price ?? it?.basicPrice ?? 0);
  const calc = round2(qty * price);
  const total = N(it?.totalPrice || calc);
  let taxable = N(it?.taxableAmount);

  const saneTaxable = taxable > 0 && taxable <= Math.max(total, calc) + 0.05;

  if (!saneTaxable) {
    const looksInclusive = total > calc + 0.05 && rate > 0;
    taxable = looksInclusive ? round2(total / (1 + rate / 100)) : calc;
  } else {
    taxable = round2(taxable);
  }

  return taxable;
}

/* =========================
   Invoice-level reconciliation
   ========================= */
function computeInvoiceHSNBreakup(order) {
  const orderDate = order?.date || order?.createdAt || order?.updatedAt;
  const isIGST = !!order?.igstTaxType;
  const items = Array.isArray(order?.orderItems) ? order.orderItems : [];

  const entries = [];

  items.forEach((it) => {
    const hsn = getHSN(it);
    const uqc = getUQC(it);
    const desc = getDesc(it);
    const qty = N(it?.qty);
    const rate = effectiveRate(orderDate, it);
    const taxable = provisionalItemTaxable(it, rate);

    let cgst = 0;
    let sgst = 0;
    let igst = 0;

    if (isIGST) {
      igst = round2((taxable * rate) / 100);
    } else {
      const half = rate / 2;
      cgst = round2((taxable * half) / 100);
      sgst = round2((taxable * half) / 100);
    }

    entries.push({
      key: `${hsn}|${rate}|${uqc}`,
      HSN_Code: hsn || "-",
      Product_Desc: desc || "",
      uqc: uqc || "",
      gstPercentage: rate,
      qty,
      taxableAmount: round2(taxable),
      cgstAmt: round2(cgst),
      sgstAmt: round2(sgst),
      igstAmt: round2(igst),
      cessAmount: 0,
      grandTotal: round2(taxable + cgst + sgst + igst),
      isChargeRow: false,
    });
  });

  const chargesTaxable = getOverallCharges(order);
  if (chargesTaxable > 0) {
    let cCgst = N(order?.ChargesCgst);
    let cSgst = N(order?.ChargesSgst);
    let cIgst = N(order?.ChargesIgst);

    let maxRate = 0;
    items.forEach((it) => {
      maxRate = Math.max(maxRate, effectiveRate(orderDate, it));
    });
    if (maxRate === 0) maxRate = 18;

    if (cCgst === 0 && cSgst === 0 && cIgst === 0) {
      if (isIGST) {
        cIgst = round2((chargesTaxable * maxRate) / 100);
      } else {
        const half = maxRate / 2;
        cCgst = round2((chargesTaxable * half) / 100);
        cSgst = round2((chargesTaxable * half) / 100);
      }
    }

    entries.push({
      key: `CHARGES@${maxRate}|${maxRate}|`,
      HSN_Code: "CHARGES",
      Product_Desc: `CHARGES @ ${maxRate}%`,
      uqc: "",
      gstPercentage: maxRate,
      qty: 0,
      taxableAmount: round2(chargesTaxable),
      cgstAmt: round2(cCgst),
      sgstAmt: round2(cSgst),
      igstAmt: round2(cIgst),
      cessAmount: 0,
      grandTotal: round2(chargesTaxable + cCgst + cSgst + cIgst),
      isChargeRow: true,
    });
  }

  const provisional = entries.reduce(
    (acc, r) => {
      acc.taxable += N(r.taxableAmount);
      acc.cgst += N(r.cgstAmt);
      acc.sgst += N(r.sgstAmt);
      acc.igst += N(r.igstAmt);
      return acc;
    },
    { taxable: 0, cgst: 0, sgst: 0, igst: 0 },
  );

  const target = {
    taxable: invoiceExactTaxable(order),
    cgst: round2(N(order?.cgstTotal)),
    sgst: round2(N(order?.sgstTotal)),
    igst: round2(N(order?.igstTotal)),
  };

  const delta = {
    taxable: round2(target.taxable - provisional.taxable),
    cgst: round2(target.cgst - provisional.cgst),
    sgst: round2(target.sgst - provisional.sgst),
    igst: round2(target.igst - provisional.igst),
  };

  if (entries.length) {
    let adjustIndex = entries.findIndex((r) => r.isChargeRow);
    if (adjustIndex < 0) {
      let bestRate = -1;
      entries.forEach((r, idx) => {
        if (N(r.gstPercentage) >= bestRate) {
          bestRate = N(r.gstPercentage);
          adjustIndex = idx;
        }
      });
    }
    if (adjustIndex < 0) adjustIndex = 0;

    const row = entries[adjustIndex];
    row.taxableAmount = round2(row.taxableAmount + delta.taxable);
    row.cgstAmt = round2(row.cgstAmt + delta.cgst);
    row.sgstAmt = round2(row.sgstAmt + delta.sgst);
    row.igstAmt = round2(row.igstAmt + delta.igst);
    row.grandTotal = round2(
      row.taxableAmount + row.cgstAmt + row.sgstAmt + row.igstAmt,
    );
  }

  const final = entries.reduce(
    (acc, r) => {
      acc.taxable += N(r.taxableAmount);
      acc.cgst += N(r.cgstAmt);
      acc.sgst += N(r.sgstAmt);
      acc.igst += N(r.igstAmt);
      return acc;
    },
    { taxable: 0, cgst: 0, sgst: 0, igst: 0 },
  );

  const result = {
    invoiceId: order?.invoiceId,
    invoiceKey: invoiceKey(order),
    partyName: order?.partyId?.CompanyName || order?.fullName || "",
    date: orderDate,
    sales: {
      taxable: invoiceExactTaxable(order),
      cgst: round2(N(order?.cgstTotal)),
      sgst: round2(N(order?.sgstTotal)),
      igst: round2(N(order?.igstTotal)),
      roundOff: invoiceExactRoundOff(order),
      grand: invoiceExactGrand(order),
    },
    hsn: {
      taxable: round2(final.taxable),
      cgst: round2(final.cgst),
      sgst: round2(final.sgst),
      igst: round2(final.igst),
      roundOff: invoiceExactRoundOff(order),
      grand: round2(
        final.taxable +
          final.cgst +
          final.sgst +
          final.igst +
          invoiceExactRoundOff(order),
      ),
    },
    rows: entries,
  };

  return result;
}

/* =========================
   Build bucket and month groups
   ========================= */
function buildBucketFromInvoices(invoiceList = []) {
  const map = new Map();
  let roundOffTotal = 0;

  invoiceList.forEach((order) => {
    const inv = computeInvoiceHSNBreakup(order);
    inv.rows.forEach((entry) => {
      const prev = map.get(entry.key) || {
        HSN_Code: entry.HSN_Code,
        Product_Desc: entry.Product_Desc,
        uqc: entry.uqc,
        gstPercentage: entry.gstPercentage,
        qty: 0,
        taxableAmount: 0,
        cgstAmt: 0,
        sgstAmt: 0,
        igstAmt: 0,
        cessAmount: 0,
        grandTotal: 0,
      };

      prev.qty += N(entry.qty);
      prev.taxableAmount = round2(prev.taxableAmount + N(entry.taxableAmount));
      prev.cgstAmt = round2(prev.cgstAmt + N(entry.cgstAmt));
      prev.sgstAmt = round2(prev.sgstAmt + N(entry.sgstAmt));
      prev.igstAmt = round2(prev.igstAmt + N(entry.igstAmt));
      prev.cessAmount = round2(prev.cessAmount + N(entry.cessAmount));
      prev.grandTotal = round2(prev.grandTotal + N(entry.grandTotal));
      if (!prev.Product_Desc && entry.Product_Desc)
        prev.Product_Desc = entry.Product_Desc;

      map.set(entry.key, prev);
    });

    roundOffTotal = round2(roundOffTotal + inv.sales.roundOff);
  });

  const rows = Array.from(map.values()).sort((a, b) => {
    const ah = String(a.HSN_Code || "");
    const bh = String(b.HSN_Code || "");
    if (ah === bh)
      return Number(a.gstPercentage || 0) - Number(b.gstPercentage || 0);
    return ah.localeCompare(bh);
  });

  const total = rows.reduce(
    (acc, r) => {
      acc.qty += N(r.qty);
      acc.taxableAmount = round2(acc.taxableAmount + N(r.taxableAmount));
      acc.cgstAmt = round2(acc.cgstAmt + N(r.cgstAmt));
      acc.sgstAmt = round2(acc.sgstAmt + N(r.sgstAmt));
      acc.igstAmt = round2(acc.igstAmt + N(r.igstAmt));
      acc.cessAmount = round2(acc.cessAmount + N(r.cessAmount));
      acc.grandTotal = round2(acc.grandTotal + N(r.grandTotal));
      return acc;
    },
    {
      __summary: true,
      HSN_Code: "Total",
      Product_Desc: "",
      uqc: "",
      gstPercentage: "",
      qty: 0,
      taxableAmount: 0,
      cgstAmt: 0,
      sgstAmt: 0,
      igstAmt: 0,
      cessAmount: 0,
      grandTotal: 0,
    },
  );

  return {
    rows: [...rows, total],
    roundOffTotal,
  };
}

function buildMonthWiseHSNGroups(invoiceList = [], fyStartYear, bucketKey) {
  const groupMap = new Map();

  invoiceList.forEach((order) => {
    const rowDate = order?.date || order?.createdAt || order?.updatedAt;
    const monthName = getMonthNameFromDate(rowDate);
    const key = `${bucketKey}__${fyStartYear}__${monthName}`;

    if (!groupMap.has(key)) {
      groupMap.set(key, {
        key,
        monthName,
        monthLabel: getMonthLabelFromDate(rowDate, fyStartYear),
        invoices: [],
      });
    }

    groupMap.get(key).invoices.push(order);
  });

  const groups = Array.from(groupMap.values()).map((g) => {
    const built = buildBucketFromInvoices(g.invoices);
    const summary = built.rows[built.rows.length - 1] || {};

    return {
      ...g,
      rows: built.rows,
      totals: {
        qty: N(summary.qty),
        taxableAmount: round2(N(summary.taxableAmount)),
        cgstAmt: round2(N(summary.cgstAmt)),
        sgstAmt: round2(N(summary.sgstAmt)),
        igstAmt: round2(N(summary.igstAmt)),
        cessAmount: round2(N(summary.cessAmount)),
        grandTotal: round2(N(summary.grandTotal) + N(built.roundOffTotal)),
        roundOff: round2(built.roundOffTotal),
      },
    };
  });

  groups.sort((a, b) => {
    const ai = getMonthSortIndex(a.monthName);
    const bi = getMonthSortIndex(b.monthName);
    if (ai !== bi) return ai - bi;
    return a.monthLabel.localeCompare(b.monthLabel);
  });

  return groups;
}

function buildSalesMonthTotals(invoiceList = [], fyStartYear) {
  const map = new Map();

  invoiceList.forEach((order) => {
    const rowDate = order?.date || order?.createdAt || order?.updatedAt;
    const monthName = getMonthNameFromDate(rowDate);
    const key = `${fyStartYear}__${monthName}`;

    if (!map.has(key)) {
      map.set(key, {
        key,
        monthName,
        monthLabel: getMonthLabelFromDate(rowDate, fyStartYear),
        taxable: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
        roundOff: 0,
        grand: 0,
      });
    }

    const prev = map.get(key);
    prev.taxable = round2(prev.taxable + invoiceExactTaxable(order));
    prev.cgst = round2(prev.cgst + N(order?.cgstTotal));
    prev.sgst = round2(prev.sgst + N(order?.sgstTotal));
    prev.igst = round2(prev.igst + N(order?.igstTotal));
    prev.roundOff = round2(prev.roundOff + invoiceExactRoundOff(order));
    prev.grand = round2(prev.grand + invoiceExactGrand(order));
  });

  return Array.from(map.values()).sort((a, b) => {
    const ai = getMonthSortIndex(a.monthName);
    const bi = getMonthSortIndex(b.monthName);
    if (ai !== bi) return ai - bi;
    return a.monthLabel.localeCompare(b.monthLabel);
  });
}

/* =========================
   Tables
   ========================= */
const COLS = [
  { key: "HSN_Code", name: "HSN" },
  { key: "Product_Desc", name: "Description" },
  { key: "uqc", name: "UQC" },
  { key: "qty", name: "Total Quantity" },
  { key: "grandTotal", name: "Total Value" },
  { key: "gstPercentage", name: "Rate" },
  { key: "taxableAmount", name: "Taxable Value" },
  { key: "sgstAmt", name: "SGST" },
  { key: "cgstAmt", name: "CGST" },
  { key: "igstAmt", name: "IGST" },
  { key: "cessAmount", name: "Cess Amount" },
];

function renderCell(col, r) {
  const key = col.key;
  if (r?.__summary) {
    if (key === "HSN_Code") return <strong>Total</strong>;
    if (
      [
        "qty",
        "grandTotal",
        "taxableAmount",
        "sgstAmt",
        "cgstAmt",
        "igstAmt",
        "cessAmount",
      ].includes(key)
    ) {
      return <strong>{num(r?.[key])}</strong>;
    }
    return "";
  }

  const val = r?.[key];
  if (
    [
      "qty",
      "grandTotal",
      "taxableAmount",
      "sgstAmt",
      "cgstAmt",
      "igstAmt",
      "cessAmount",
    ].includes(key)
  ) {
    return num(val);
  }
  return val || "";
}

function buildExportRowsFromMonthGroups(caption, monthGroups) {
  const rows = [];

  monthGroups.forEach((group) => {
    rows.push({
      Bucket: caption,
      Month: group.monthLabel,
      HSN: "MONTH TOTAL",
      Description: "",
      UQC: "",
      "Total Quantity": num(group.totals.qty),
      "Total Value": num(group.totals.grandTotal),
      Rate: "",
      "Taxable Value": num(group.totals.taxableAmount),
      SGST: num(group.totals.sgstAmt),
      CGST: num(group.totals.cgstAmt),
      IGST: num(group.totals.igstAmt),
      "Cess Amount": num(group.totals.cessAmount),
      "Round Off": num(group.totals.roundOff),
    });

    (group.rows || [])
      .filter((r) => !r?.__summary)
      .forEach((r) => {
        rows.push({
          Bucket: caption,
          Month: group.monthLabel,
          HSN: r?.HSN_Code || "",
          Description: r?.Product_Desc || "",
          UQC: r?.uqc || "",
          "Total Quantity": num(r?.qty),
          "Total Value": num(r?.grandTotal),
          Rate: r?.gstPercentage ?? "",
          "Taxable Value": num(r?.taxableAmount),
          SGST: num(r?.sgstAmt),
          CGST: num(r?.cgstAmt),
          IGST: num(r?.igstAmt),
          "Cess Amount": num(r?.cessAmount),
          "Round Off": "",
        });
      });
  });

  return rows;
}

function MonthWiseHSNTable({
  caption,
  monthGroups,
  monthOpenMap,
  onToggleMonth,
  perm,
  rangeLabel,
}) {
  const exportRows = useMemo(
    () => buildExportRowsFromMonthGroups(caption, monthGroups),
    [caption, monthGroups],
  );

  const exportName = `HSN_${caption}_${rangeLabel
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "_")}`;

  const finalTotals = useMemo(
    () =>
      monthGroups.reduce(
        (acc, g) => {
          acc.qty += N(g.totals.qty);
          acc.taxableAmount = round2(
            acc.taxableAmount + N(g.totals.taxableAmount),
          );
          acc.cgstAmt = round2(acc.cgstAmt + N(g.totals.cgstAmt));
          acc.sgstAmt = round2(acc.sgstAmt + N(g.totals.sgstAmt));
          acc.igstAmt = round2(acc.igstAmt + N(g.totals.igstAmt));
          acc.cessAmount = round2(acc.cessAmount + N(g.totals.cessAmount));
          acc.grandTotal = round2(acc.grandTotal + N(g.totals.grandTotal));
          acc.roundOff = round2(acc.roundOff + N(g.totals.roundOff));
          return acc;
        },
        {
          qty: 0,
          taxableAmount: 0,
          cgstAmt: 0,
          sgstAmt: 0,
          igstAmt: 0,
          cessAmount: 0,
          grandTotal: 0,
          roundOff: 0,
        },
      ),
    [monthGroups],
  );

  const onPdf = () => {
    if (!exportRows.length) return;
    const headers = Object.keys(exportRows[0]);
    const doc = new jsPDF("landscape");
    doc.setFontSize(14);
    doc.text(`${caption} — ${rangeLabel}`, 14, 16);
    doc.autoTable({
      head: [headers],
      body: exportRows.map((r) => headers.map((h) => toStr(r[h] ?? ""))),
      startY: 22,
      styles: {
        fontSize: 9,
        cellPadding: 2,
        halign: "center",
        valign: "middle",
      },
      headStyles: { fillColor: [244, 247, 246], textColor: [0, 0, 0] },
      alternateRowStyles: { fillColor: [248, 250, 255] },
    });
    doc.save(`${exportName}.pdf`);
  };

  const onXlsx = () => {
    const ws = XLSX.utils.json_to_sheet(
      exportRows.length ? exportRows : [{ message: "No data" }],
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "HSN");
    XLSX.writeFile(wb, `${exportName}.xlsx`);
  };

  return (
    <>
      <div className="section-title">
        {caption}{" "}
        <span className="pill">
          {monthGroups.reduce(
            (a, g) => a + Math.max(0, (g.rows?.length || 1) - 1),
            0,
          )}
        </span>
        <div style={{ flex: 1 }} />
        {perm?.Download && (
          <>
            <Button
              outline
              color="primary"
              className="hsnIconBtn"
              onClick={onPdf}
              title={`${caption} — Download PDF`}
              style={{ marginRight: 8 }}
            >
              <ImFilePdf />
            </Button>
            <Button
              outline
              color="primary"
              className="hsnIconBtn"
              onClick={onXlsx}
              title={`${caption} — Download Excel`}
            >
              <ImFileExcel />
            </Button>
          </>
        )}
      </div>

      <div className="pa-table-wrap" style={{ marginBottom: 12 }}>
        <table className="pa-table">
          <thead>
            <tr>
              <th>#</th>
              {COLS.map((c) => (
                <th key={c.key}>{c.name}</th>
              ))}
              <th>Round Off</th>
            </tr>
          </thead>
          <tbody>
            {monthGroups.length ? (
              <>
                {monthGroups.map((group) => (
                  <React.Fragment key={group.key}>
                    <tr
                      className="hsn-month-row"
                      onClick={() => onToggleMonth(group.key)}
                    >
                      <td>
                        <div className="hsn-month-cell">
                          {monthOpenMap[group.key] ? (
                            <FaChevronDown />
                          ) : (
                            <FaChevronRight />
                          )}
                          <span>{group.monthLabel}</span>
                        </div>
                      </td>

                      {COLS.map((c) => {
                        if (c.key === "HSN_Code") {
                          return (
                            <td key={c.key}>
                              <span className="hsn-month-muted">
                                Month Total
                              </span>
                            </td>
                          );
                        }
                        if (c.key === "qty")
                          return <td key={c.key}>{num(group.totals.qty)}</td>;
                        if (c.key === "grandTotal")
                          return (
                            <td key={c.key}>{num(group.totals.grandTotal)}</td>
                          );
                        if (c.key === "taxableAmount")
                          return (
                            <td key={c.key}>
                              {num(group.totals.taxableAmount)}
                            </td>
                          );
                        if (c.key === "sgstAmt")
                          return (
                            <td key={c.key}>{num(group.totals.sgstAmt)}</td>
                          );
                        if (c.key === "cgstAmt")
                          return (
                            <td key={c.key}>{num(group.totals.cgstAmt)}</td>
                          );
                        if (c.key === "igstAmt")
                          return (
                            <td key={c.key}>{num(group.totals.igstAmt)}</td>
                          );
                        if (c.key === "cessAmount")
                          return (
                            <td key={c.key}>{num(group.totals.cessAmount)}</td>
                          );
                        return <td key={c.key} />;
                      })}
                      <td>{num(group.totals.roundOff)}</td>
                    </tr>

                    {monthOpenMap[group.key] &&
                      group.rows.map((r, i) => (
                        <tr key={`${group.key}-${r?.HSN_Code || "sum"}-${i}`}>
                          <td>{r?.__summary ? "" : i + 1}</td>
                          {COLS.map((c) => (
                            <td key={c.key}>{renderCell(c, r)}</td>
                          ))}
                          <td>{r?.__summary ? "" : ""}</td>
                        </tr>
                      ))}
                  </React.Fragment>
                ))}

                <tr className="hsn-final-row">
                  <td />
                  {COLS.map((c) => {
                    if (c.key === "HSN_Code")
                      return <td key={c.key}>Final Total</td>;
                    if (c.key === "qty")
                      return <td key={c.key}>{num(finalTotals.qty)}</td>;
                    if (c.key === "grandTotal")
                      return <td key={c.key}>{num(finalTotals.grandTotal)}</td>;
                    if (c.key === "taxableAmount")
                      return (
                        <td key={c.key}>{num(finalTotals.taxableAmount)}</td>
                      );
                    if (c.key === "sgstAmt")
                      return <td key={c.key}>{num(finalTotals.sgstAmt)}</td>;
                    if (c.key === "cgstAmt")
                      return <td key={c.key}>{num(finalTotals.cgstAmt)}</td>;
                    if (c.key === "igstAmt")
                      return <td key={c.key}>{num(finalTotals.igstAmt)}</td>;
                    if (c.key === "cessAmount")
                      return <td key={c.key}>{num(finalTotals.cessAmount)}</td>;
                    return <td key={c.key} />;
                  })}
                  <td>{num(finalTotals.roundOff)}</td>
                </tr>
              </>
            ) : (
              <tr>
                <td
                  colSpan={COLS.length + 2}
                  style={{ padding: 16 }}
                  className="muted"
                >
                  No records
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

function ReconciliationTable({ monthCompareRows, finalCompare }) {
  return (
    <>
      <div className="section-title">
        Month-wise Reconciliation{" "}
        <span className="pill">{monthCompareRows.length}</span>
      </div>
      <div className="pa-table-wrap" style={{ marginBottom: 12 }}>
        <table className="reco-table">
          <thead>
            <tr>
              <th>Month</th>
              <th>HSN Taxable</th>
              <th>Sales Taxable</th>
              <th>HSN CGST</th>
              <th>Sales CGST</th>
              <th>HSN SGST</th>
              <th>Sales SGST</th>
              <th>HSN IGST</th>
              <th>Sales IGST</th>
              <th>HSN Round Off</th>
              <th>Sales Round Off</th>
              <th>HSN Grand</th>
              <th>Sales Grand</th>
            </tr>
          </thead>
          <tbody>
            {monthCompareRows.map((r) => (
              <tr key={r.monthLabel}>
                <td>{r.monthLabel}</td>
                <td className={mismatchClass(r.hsn.taxable, r.sales.taxable)}>
                  {num(r.hsn.taxable)}
                </td>
                <td className={mismatchClass(r.hsn.taxable, r.sales.taxable)}>
                  {num(r.sales.taxable)}
                </td>
                <td className={mismatchClass(r.hsn.cgst, r.sales.cgst)}>
                  {num(r.hsn.cgst)}
                </td>
                <td className={mismatchClass(r.hsn.cgst, r.sales.cgst)}>
                  {num(r.sales.cgst)}
                </td>
                <td className={mismatchClass(r.hsn.sgst, r.sales.sgst)}>
                  {num(r.hsn.sgst)}
                </td>
                <td className={mismatchClass(r.hsn.sgst, r.sales.sgst)}>
                  {num(r.sales.sgst)}
                </td>
                <td className={mismatchClass(r.hsn.igst, r.sales.igst)}>
                  {num(r.hsn.igst)}
                </td>
                <td className={mismatchClass(r.hsn.igst, r.sales.igst)}>
                  {num(r.sales.igst)}
                </td>
                <td className={mismatchClass(r.hsn.roundOff, r.sales.roundOff)}>
                  {num(r.hsn.roundOff)}
                </td>
                <td className={mismatchClass(r.hsn.roundOff, r.sales.roundOff)}>
                  {num(r.sales.roundOff)}
                </td>
                <td className={mismatchClass(r.hsn.grand, r.sales.grand)}>
                  {num(r.hsn.grand)}
                </td>
                <td className={mismatchClass(r.hsn.grand, r.sales.grand)}>
                  {num(r.sales.grand)}
                </td>
              </tr>
            ))}

            <tr className="hsn-final-row">
              <td>FINAL TOTAL</td>
              <td
                className={mismatchClass(
                  finalCompare.hsn.taxable,
                  finalCompare.sales.taxable,
                )}
              >
                {num(finalCompare.hsn.taxable)}
              </td>
              <td
                className={mismatchClass(
                  finalCompare.hsn.taxable,
                  finalCompare.sales.taxable,
                )}
              >
                {num(finalCompare.sales.taxable)}
              </td>
              <td
                className={mismatchClass(
                  finalCompare.hsn.cgst,
                  finalCompare.sales.cgst,
                )}
              >
                {num(finalCompare.hsn.cgst)}
              </td>
              <td
                className={mismatchClass(
                  finalCompare.hsn.cgst,
                  finalCompare.sales.cgst,
                )}
              >
                {num(finalCompare.sales.cgst)}
              </td>
              <td
                className={mismatchClass(
                  finalCompare.hsn.sgst,
                  finalCompare.sales.sgst,
                )}
              >
                {num(finalCompare.hsn.sgst)}
              </td>
              <td
                className={mismatchClass(
                  finalCompare.hsn.sgst,
                  finalCompare.sales.sgst,
                )}
              >
                {num(finalCompare.sales.sgst)}
              </td>
              <td
                className={mismatchClass(
                  finalCompare.hsn.igst,
                  finalCompare.sales.igst,
                )}
              >
                {num(finalCompare.hsn.igst)}
              </td>
              <td
                className={mismatchClass(
                  finalCompare.hsn.igst,
                  finalCompare.sales.igst,
                )}
              >
                {num(finalCompare.sales.igst)}
              </td>
              <td
                className={mismatchClass(
                  finalCompare.hsn.roundOff,
                  finalCompare.sales.roundOff,
                )}
              >
                {num(finalCompare.hsn.roundOff)}
              </td>
              <td
                className={mismatchClass(
                  finalCompare.hsn.roundOff,
                  finalCompare.sales.roundOff,
                )}
              >
                {num(finalCompare.sales.roundOff)}
              </td>
              <td
                className={mismatchClass(
                  finalCompare.hsn.grand,
                  finalCompare.sales.grand,
                )}
              >
                {num(finalCompare.hsn.grand)}
              </td>
              <td
                className={mismatchClass(
                  finalCompare.hsn.grand,
                  finalCompare.sales.grand,
                )}
              >
                {num(finalCompare.sales.grand)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
}

function MismatchDetailsTable({ rows }) {
  if (!rows.length) return null;

  return (
    <>
      <div className="section-title">
        Mismatch Details <span className="pill">{rows.length}</span>
      </div>
      <div className="pa-table-wrap" style={{ marginBottom: 12 }}>
        <table className="reco-table">
          <thead>
            <tr>
              <th>Invoice</th>
              <th>Date</th>
              <th>Party</th>
              <th>Field</th>
              <th>HSN</th>
              <th>Sales</th>
              <th>Difference</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.invoiceKey}-${r.field}-${i}`}>
                <td>{r.invoiceId}</td>
                <td>{r.date}</td>
                <td>{r.partyName}</td>
                <td>{r.field}</td>
                <td className="hsn-mismatch">{num(r.hsn)}</td>
                <td className="hsn-mismatch">{num(r.sales)}</td>
                <td className="hsn-mismatch">{num(r.diff)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/* =========================
   Component
   ========================= */
export default function HSNSaleSummaryNew() {
  const [loading, setLoading] = useState(true);
  const [perm, setPerm] = useState({ View: true, Download: true });
  const [masterShow, setMasterShow] = useState(false);

  const today = new Date();
  const defaultFY = getFYStartYear(today);

  const [mode, setMode] = useState("yearly");
  const [mMonth, setMMonth] = useState(4);
  const [mYear, setMYear] = useState(today.getFullYear());
  const [qQuarter, setQQuarter] = useState("Q1");
  const [qYear, setQYear] = useState(defaultFY);
  const [hHalf, setHHalf] = useState("H1");
  const [hYear, setHYear] = useState(defaultFY);
  const [yYear, setYYear] = useState(defaultFY);

  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);

  const [regularInvoices, setRegularInvoices] = useState([]);
  const [unregisterInvoices, setUnregisterInvoices] = useState([]);

  const [monthOpenMap, setMonthOpenMap] = useState({});

  const activeFY =
    mode === "monthly"
      ? getFYStartYear(new Date(mYear, mMonth - 1, 1))
      : mode === "quarterly"
        ? qYear
        : mode === "halfyearly"
          ? hYear
          : yYear;

  const currentRangeLabel = useMemo(() => {
    if (mode === "monthly") {
      const fy = getFYStartYear(new Date(mYear, mMonth - 1, 1));
      return formatCurrentRangeLabel("month", fy, mMonth, "", "");
    }
    if (mode === "quarterly") {
      return formatCurrentRangeLabel("quarter", qYear, "", qQuarter, "");
    }
    if (mode === "halfyearly") {
      return formatCurrentRangeLabel("half", hYear, "", "", hHalf);
    }
    return formatCurrentRangeLabel("year", yYear, "", "", "");
  }, [mode, mMonth, mYear, qQuarter, qYear, hHalf, hYear, yYear]);

  const yearOptionsFY = useMemo(() => {
    const arr = [];
    for (let y = defaultFY - 2; y <= defaultFY + 1; y++) arr.push(y);
    return arr;
  }, [defaultFY]);

  const loadSalesInvoicesByDatabase = async (baseDatabase) => {
    const selectedDatabase = buildSelectedDatabase(baseDatabase);

    const salesRes = await _Get(
      view_create_order_history_database,
      selectedDatabase,
    );

    const allSalesInvoices = getSalesOrderListFromResponse(salesRes);

    const completedInvoices = allSalesInvoices.filter(isCompletedInvoice);

    const { b2bData, b2csData, b2clData } =
      splitInvoicesIntoGstBuckets(completedInvoices);

    setRegularInvoices(b2bData);
    setUnregisterInvoices([...b2csData, ...b2clData]);
  };

  useEffect(() => {
    injectOnce();
    (async () => {
      setLoading(true);
      try {
        const inside = CheckPermission("GSTR 1");
        setPerm(inside || { View: true, Download: true });

        const user = getUserData();
        if (user?.rolename?.roleName === "MASTER") setMasterShow(true);

        await loadSalesInvoicesByDatabase(user?.database);
      } catch (e) {
        console.error(e);
        setRegularInvoices([]);
        setUnregisterInvoices([]);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const periodPredicate = useMemo(() => {
    if (mode === "monthly") {
      const fy = getFYStartYear(new Date(mYear, mMonth - 1, 1));
      return (o) =>
        inFinancialPeriod(
          o?.date || o?.createdAt || o?.updatedAt,
          fy,
          "month",
          mMonth,
          "",
          "",
        );
    }
    if (mode === "quarterly") {
      return (o) =>
        inFinancialPeriod(
          o?.date || o?.createdAt || o?.updatedAt,
          qYear,
          "quarter",
          "",
          qQuarter,
          "",
        );
    }
    if (mode === "halfyearly") {
      return (o) =>
        inFinancialPeriod(
          o?.date || o?.createdAt || o?.updatedAt,
          hYear,
          "half",
          "",
          "",
          hHalf,
        );
    }
    return (o) =>
      inFinancialPeriod(
        o?.date || o?.createdAt || o?.updatedAt,
        yYear,
        "year",
        "",
        "",
        "",
      );
  }, [mode, mMonth, mYear, qQuarter, qYear, hHalf, hYear, yYear]);

  const searchPredicate = useMemo(() => {
    const term = deferredSearch.trim().toLowerCase();
    if (!term) return () => true;

    return (order) => {
      const items = Array.isArray(order?.orderItems) ? order.orderItems : [];
      const itemBag = items
        .map((it) =>
          [
            getHSN(it),
            getDesc(it),
            getUQC(it),
            it?.qty,
            it?.price,
            it?.taxableAmount,
          ]
            .map(toStr)
            .join(" "),
        )
        .join(" ");

      const bag = [
        order?.invoiceId,
        order?.partyId?.CompanyName,
        order?.partyId?.gstNumber,
        order?.amount,
        order?.grandTotal,
        itemBag,
      ]
        .map(toStr)
        .join(" ")
        .toLowerCase();

      return bag.includes(term);
    };
  }, [deferredSearch]);

  const regularFilteredInvoices = useMemo(
    () => regularInvoices.filter(periodPredicate).filter(searchPredicate),
    [regularInvoices, periodPredicate, searchPredicate],
  );

  const unregisterFilteredInvoices = useMemo(
    () => unregisterInvoices.filter(periodPredicate).filter(searchPredicate),
    [unregisterInvoices, periodPredicate, searchPredicate],
  );

  const allFilteredInvoices = useMemo(
    () => [...regularFilteredInvoices, ...unregisterFilteredInvoices],
    [regularFilteredInvoices, unregisterFilteredInvoices],
  );

  const regularMonthGroups = useMemo(
    () => buildMonthWiseHSNGroups(regularFilteredInvoices, activeFY, "Regular"),
    [regularFilteredInvoices, activeFY],
  );

  const unregisterMonthGroups = useMemo(
    () =>
      buildMonthWiseHSNGroups(
        unregisterFilteredInvoices,
        activeFY,
        "UnRegister",
      ),
    [unregisterFilteredInvoices, activeFY],
  );

  const toggleMonth = (key) => {
    setMonthOpenMap((prev) => {
      const isOpen = !!prev[key];
      const next = {};
      Object.keys(prev).forEach((k) => {
        next[k] = false;
      });
      next[key] = !isOpen;
      return next;
    });
  };

  useEffect(() => {
    const next = {};
    [...regularMonthGroups, ...unregisterMonthGroups].forEach((g) => {
      next[g.key] = false;
    });
    setMonthOpenMap(next);
  }, [regularMonthGroups, unregisterMonthGroups]);

  const combinedTotals = useMemo(() => {
    const allGroups = [...regularMonthGroups, ...unregisterMonthGroups];
    return allGroups.reduce(
      (acc, g) => {
        acc.qty += N(g.totals.qty);
        acc.taxableAmount = round2(
          acc.taxableAmount + N(g.totals.taxableAmount),
        );
        acc.cgstAmt = round2(acc.cgstAmt + N(g.totals.cgstAmt));
        acc.sgstAmt = round2(acc.sgstAmt + N(g.totals.sgstAmt));
        acc.igstAmt = round2(acc.igstAmt + N(g.totals.igstAmt));
        acc.roundOff = round2(acc.roundOff + N(g.totals.roundOff));
        acc.grandTotal = round2(acc.grandTotal + N(g.totals.grandTotal));
        return acc;
      },
      {
        qty: 0,
        taxableAmount: 0,
        cgstAmt: 0,
        sgstAmt: 0,
        igstAmt: 0,
        roundOff: 0,
        grandTotal: 0,
      },
    );
  }, [regularMonthGroups, unregisterMonthGroups]);

  const salesMonthTotals = useMemo(
    () => buildSalesMonthTotals(allFilteredInvoices, activeFY),
    [allFilteredInvoices, activeFY],
  );

  const completedTotals = useMemo(() => {
    let taxable = 0;
    let cgst = 0;
    let sgst = 0;
    let igst = 0;
    let roundOff = 0;
    let grand = 0;

    allFilteredInvoices.forEach((o) => {
      taxable += invoiceExactTaxable(o);
      cgst += N(o?.cgstTotal);
      sgst += N(o?.sgstTotal);
      igst += N(o?.igstTotal);
      roundOff += invoiceExactRoundOff(o);
      grand += invoiceExactGrand(o);
    });

    return {
      taxable: round2(taxable),
      cgst: round2(cgst),
      sgst: round2(sgst),
      igst: round2(igst),
      roundOff: round2(roundOff),
      grand: round2(grand),
    };
  }, [allFilteredInvoices]);

  const monthCompareRows = useMemo(() => {
    const hsnMap = new Map();

    [...regularMonthGroups, ...unregisterMonthGroups].forEach((g) => {
      const prev = hsnMap.get(g.monthLabel) || {
        monthLabel: g.monthLabel,
        hsn: { taxable: 0, cgst: 0, sgst: 0, igst: 0, roundOff: 0, grand: 0 },
        sales: { taxable: 0, cgst: 0, sgst: 0, igst: 0, roundOff: 0, grand: 0 },
      };
      prev.hsn.taxable = round2(prev.hsn.taxable + N(g.totals.taxableAmount));
      prev.hsn.cgst = round2(prev.hsn.cgst + N(g.totals.cgstAmt));
      prev.hsn.sgst = round2(prev.hsn.sgst + N(g.totals.sgstAmt));
      prev.hsn.igst = round2(prev.hsn.igst + N(g.totals.igstAmt));
      prev.hsn.roundOff = round2(prev.hsn.roundOff + N(g.totals.roundOff));
      prev.hsn.grand = round2(prev.hsn.grand + N(g.totals.grandTotal));
      hsnMap.set(g.monthLabel, prev);
    });

    salesMonthTotals.forEach((g) => {
      const prev = hsnMap.get(g.monthLabel) || {
        monthLabel: g.monthLabel,
        hsn: { taxable: 0, cgst: 0, sgst: 0, igst: 0, roundOff: 0, grand: 0 },
        sales: { taxable: 0, cgst: 0, sgst: 0, igst: 0, roundOff: 0, grand: 0 },
      };
      prev.sales.taxable = round2(prev.sales.taxable + N(g.taxable));
      prev.sales.cgst = round2(prev.sales.cgst + N(g.cgst));
      prev.sales.sgst = round2(prev.sales.sgst + N(g.sgst));
      prev.sales.igst = round2(prev.sales.igst + N(g.igst));
      prev.sales.roundOff = round2(prev.sales.roundOff + N(g.roundOff));
      prev.sales.grand = round2(prev.sales.grand + N(g.grand));
      hsnMap.set(g.monthLabel, prev);
    });

    return Array.from(hsnMap.values()).sort((a, b) => {
      const am = a.monthLabel.split(" ")[0];
      const bm = b.monthLabel.split(" ")[0];
      const ai = getMonthSortIndex(am);
      const bi = getMonthSortIndex(bm);
      if (ai !== bi) return ai - bi;
      return a.monthLabel.localeCompare(b.monthLabel);
    });
  }, [regularMonthGroups, unregisterMonthGroups, salesMonthTotals]);

  const finalCompare = useMemo(
    () => ({
      hsn: {
        taxable: combinedTotals.taxableAmount,
        cgst: combinedTotals.cgstAmt,
        sgst: combinedTotals.sgstAmt,
        igst: combinedTotals.igstAmt,
        roundOff: combinedTotals.roundOff,
        grand: combinedTotals.grandTotal,
      },
      sales: {
        taxable: completedTotals.taxable,
        cgst: completedTotals.cgst,
        sgst: completedTotals.sgst,
        igst: completedTotals.igst,
        roundOff: completedTotals.roundOff,
        grand: completedTotals.grand,
      },
    }),
    [combinedTotals, completedTotals],
  );

  const invoiceMismatchRows = useMemo(() => {
    const rows = [];

    allFilteredInvoices.forEach((order) => {
      const rec = computeInvoiceHSNBreakup(order);
      const fields = [
        { key: "taxable", label: "Taxable Value" },
        { key: "cgst", label: "CGST" },
        { key: "sgst", label: "SGST" },
        { key: "igst", label: "IGST" },
        { key: "roundOff", label: "Round Off" },
        { key: "grand", label: "Grand Total" },
      ];

      fields.forEach((f) => {
        const h = rec.hsn[f.key];
        const s = rec.sales[f.key];
        if (!sameNum(h, s)) {
          rows.push({
            invoiceId: order?.invoiceId || "",
            invoiceKey: invoiceKey(order),
            date: order?.date ? String(order.date).split("T")[0] : "",
            partyName: order?.partyId?.CompanyName || order?.fullName || "",
            field: f.label,
            hsn: h,
            sales: s,
            diff: round2(h - s),
          });
        }
      });
    });

    return rows.sort((a, b) => {
      const da = new Date(a.date || 0).getTime();
      const db = new Date(b.date || 0).getTime();
      if (da !== db) return db - da;
      return String(a.invoiceId).localeCompare(String(b.invoiceId));
    });
  }, [allFilteredInvoices]);

  const mismatchKeys = useMemo(() => {
    const diffs = [];
    if (!sameNum(finalCompare.hsn.grand, finalCompare.sales.grand))
      diffs.push("Grand Total");
    if (!sameNum(finalCompare.hsn.taxable, finalCompare.sales.taxable))
      diffs.push("Taxable Value");
    if (!sameNum(finalCompare.hsn.igst, finalCompare.sales.igst))
      diffs.push("IGST");
    if (!sameNum(finalCompare.hsn.cgst, finalCompare.sales.cgst))
      diffs.push("CGST");
    if (!sameNum(finalCompare.hsn.sgst, finalCompare.sales.sgst))
      diffs.push("SGST");
    if (!sameNum(finalCompare.hsn.roundOff, finalCompare.sales.roundOff))
      diffs.push("Round Off");
    return diffs;
  }, [finalCompare]);

  const rowCount = useMemo(() => {
    const countBucket = (groups) =>
      groups.reduce((a, g) => {
        const rows = (g.rows || []).filter((r) => !r?.__summary).length;
        return a + rows;
      }, 0);
    return countBucket(regularMonthGroups) + countBucket(unregisterMonthGroups);
  }, [regularMonthGroups, unregisterMonthGroups]);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading…
        </Spinner>
      </div>
    );
  }

  return (
    <div className="hsn-soft-bg">
      <Row className="mt-1">
        <Col sm="12">
          <Card className="hsnHdrCard">
            <div className="hsnHdrRow">
              {masterShow ? (
                <div style={{ minWidth: 240 }}>
                  <SuperAdminUI
                    onDropdownChange={(v) =>
                      localStorage.setItem(
                        "SuperadminIdByMaster",
                        JSON.stringify(v),
                      )
                    }
                    onSubmit={async (e) => {
                      e.preventDefault();

                      const picked = JSON.parse(
                        localStorage.getItem("SuperadminIdByMaster") || "null",
                      );
                      if (!picked) return;

                      const [, db] = String(picked).split(" ");

                      try {
                        setLoading(true);
                        await loadSalesInvoicesByDatabase(db);
                      } catch (err) {
                        console.error(err);
                        setRegularInvoices([]);
                        setUnregisterInvoices([]);
                      } finally {
                        setLoading(false);
                      }
                    }}
                  />
                </div>
              ) : null}

              <div className="hsnField wide">
                <div className="hsnLbl">Mode</div>
                <Input
                  type="select"
                  className="hsnCtl"
                  value={mode}
                  onChange={(e) => setMode(e.target.value)}
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly (FY)</option>
                  <option value="halfyearly">Half-Yearly (FY)</option>
                  <option value="yearly">Yearly (FY)</option>
                </Input>
              </div>

              {mode === "monthly" && (
                <>
                  <div className="hsnField small">
                    <div className="hsnLbl">Month</div>
                    <Input
                      type="select"
                      className="hsnCtl"
                      value={mMonth}
                      onChange={(e) =>
                        setMMonth(parseInt(e.target.value || "4", 10))
                      }
                    >
                      {FY_MONTHS.map((m) => (
                        <option key={m.value} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </Input>
                  </div>
                  <div className="hsnField wide">
                    <div className="hsnLbl">Year</div>
                    <Input
                      type="number"
                      className="hsnCtl"
                      value={mYear}
                      onChange={(e) =>
                        setMYear(
                          parseInt(
                            e.target.value || String(today.getFullYear()),
                            10,
                          ),
                        )
                      }
                    />
                  </div>
                </>
              )}

              {mode === "quarterly" && (
                <>
                  <div className="hsnField wide">
                    <div className="hsnLbl">Quarter</div>
                    <Input
                      type="select"
                      className="hsnCtl"
                      value={qQuarter}
                      onChange={(e) => setQQuarter(e.target.value)}
                    >
                      {FY_QUARTERS.map((q) => (
                        <option key={q.value} value={q.value}>
                          {q.label}
                        </option>
                      ))}
                    </Input>
                  </div>
                  <div className="hsnField wide">
                    <div className="hsnLbl">FY Start</div>
                    <Input
                      type="select"
                      className="hsnCtl"
                      value={qYear}
                      onChange={(e) =>
                        setQYear(
                          parseInt(e.target.value || String(defaultFY), 10),
                        )
                      }
                    >
                      {yearOptionsFY.map((y) => (
                        <option key={y} value={y}>
                          {`${y}-${String(y + 1).slice(-2)}`}
                        </option>
                      ))}
                    </Input>
                  </div>
                </>
              )}

              {mode === "halfyearly" && (
                <>
                  <div className="hsnField wide">
                    <div className="hsnLbl">Half</div>
                    <Input
                      type="select"
                      className="hsnCtl"
                      value={hHalf}
                      onChange={(e) => setHHalf(e.target.value)}
                    >
                      <option value="H1">H1 (Apr–Sep)</option>
                      <option value="H2">H2 (Oct–Mar)</option>
                    </Input>
                  </div>
                  <div className="hsnField wide">
                    <div className="hsnLbl">FY Start</div>
                    <Input
                      type="select"
                      className="hsnCtl"
                      value={hYear}
                      onChange={(e) =>
                        setHYear(
                          parseInt(e.target.value || String(defaultFY), 10),
                        )
                      }
                    >
                      {yearOptionsFY.map((y) => (
                        <option key={y} value={y}>
                          {`${y}-${String(y + 1).slice(-2)}`}
                        </option>
                      ))}
                    </Input>
                  </div>
                </>
              )}

              {mode === "yearly" && (
                <div className="hsnField wide">
                  <div className="hsnLbl">FY</div>
                  <Input
                    type="select"
                    className="hsnCtl"
                    value={yYear}
                    onChange={(e) =>
                      setYYear(
                        parseInt(e.target.value || String(defaultFY), 10),
                      )
                    }
                  >
                    {yearOptionsFY.map((y) => (
                      <option key={y} value={y}>
                        {`${y}-${String(y + 1).slice(-2)}`}
                      </option>
                    ))}
                  </Input>
                </div>
              )}

              <div className="hsnField wide">
                <div className="hsnLbl">Search</div>
                <Input
                  className="hsnCtl"
                  placeholder="Search…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <div className="hsnSpacer" />

              <div className="hsnMetaMuted">{currentRangeLabel}</div>
              <div className="hsnMeta">{rowCount}</div>
            </div>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col sm="12">
          <div className="sum-bar">
            <div className="section-title" style={{ marginTop: 0 }}>
              HSN Combined Totals (Current Filter)
            </div>
            <div className="sum-grid-6">
              <div
                className={`sum-box ${mismatchClass(finalCompare.hsn.grand, finalCompare.sales.grand)}`}
              >
                <div className="sum-k">Grand Total</div>
                <div className="sum-v">{num(combinedTotals.grandTotal)}</div>
              </div>
              <div
                className={`sum-box ${mismatchClass(finalCompare.hsn.taxable, finalCompare.sales.taxable)}`}
              >
                <div className="sum-k">Taxable Value</div>
                <div className="sum-v">{num(combinedTotals.taxableAmount)}</div>
              </div>
              <div
                className={`sum-box ${mismatchClass(finalCompare.hsn.igst, finalCompare.sales.igst)}`}
              >
                <div className="sum-k">IGST</div>
                <div className="sum-v">{num(combinedTotals.igstAmt)}</div>
              </div>
              <div
                className={`sum-box ${mismatchClass(finalCompare.hsn.cgst, finalCompare.sales.cgst)}`}
              >
                <div className="sum-k">CGST</div>
                <div className="sum-v">{num(combinedTotals.cgstAmt)}</div>
              </div>
              <div
                className={`sum-box ${mismatchClass(finalCompare.hsn.sgst, finalCompare.sales.sgst)}`}
              >
                <div className="sum-k">SGST</div>
                <div className="sum-v">{num(combinedTotals.sgstAmt)}</div>
              </div>
              <div
                className={`sum-box ${mismatchClass(finalCompare.hsn.roundOff, finalCompare.sales.roundOff)}`}
              >
                <div className="sum-k">Round Off</div>
                <div className="sum-v">{num(combinedTotals.roundOff)}</div>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col sm="12">
          <div className="sum-bar">
            <div className="section-title" style={{ marginTop: 0 }}>
              Sales Invoice Totals (Same Invoices / Current Filter)
            </div>
            <div className="sum-grid-6">
              <div
                className={`sum-box ${mismatchClass(finalCompare.hsn.grand, finalCompare.sales.grand)}`}
              >
                <div className="sum-k">Grand Total</div>
                <div className="sum-v">{num(completedTotals.grand)}</div>
              </div>
              <div
                className={`sum-box ${mismatchClass(finalCompare.hsn.taxable, finalCompare.sales.taxable)}`}
              >
                <div className="sum-k">Taxable Value</div>
                <div className="sum-v">{num(completedTotals.taxable)}</div>
              </div>
              <div
                className={`sum-box ${mismatchClass(finalCompare.hsn.igst, finalCompare.sales.igst)}`}
              >
                <div className="sum-k">IGST</div>
                <div className="sum-v">{num(completedTotals.igst)}</div>
              </div>
              <div
                className={`sum-box ${mismatchClass(finalCompare.hsn.cgst, finalCompare.sales.cgst)}`}
              >
                <div className="sum-k">CGST</div>
                <div className="sum-v">{num(completedTotals.cgst)}</div>
              </div>
              <div
                className={`sum-box ${mismatchClass(finalCompare.hsn.sgst, finalCompare.sales.sgst)}`}
              >
                <div className="sum-k">SGST</div>
                <div className="sum-v">{num(completedTotals.sgst)}</div>
              </div>
              <div
                className={`sum-box ${mismatchClass(finalCompare.hsn.roundOff, finalCompare.sales.roundOff)}`}
              >
                <div className="sum-k">Round Off</div>
                <div className="sum-v">{num(completedTotals.roundOff)}</div>
              </div>
            </div>
            {mismatchKeys.length > 0 ? (
              <div className="error-bar">
                RUNNING ERROR — mismatch in: {mismatchKeys.join(", ")}
              </div>
            ) : (
              <div className="ok-bar">
                ALL FIGURES MATCHED WITH SALES INVOICE
              </div>
            )}
          </div>
        </Col>
      </Row>

      <Row className="mt-1">
        <Col sm="12">
          <Card className="list-card">
            <CardBody style={{ padding: 8 }}>
              <ReconciliationTable
                monthCompareRows={monthCompareRows}
                finalCompare={finalCompare}
              />

              <MismatchDetailsTable rows={invoiceMismatchRows} />

              <MonthWiseHSNTable
                caption="Regular"
                monthGroups={regularMonthGroups}
                monthOpenMap={monthOpenMap}
                onToggleMonth={toggleMonth}
                perm={perm}
                rangeLabel={currentRangeLabel}
              />

              <MonthWiseHSNTable
                caption="UnRegister"
                monthGroups={unregisterMonthGroups}
                monthOpenMap={monthOpenMap}
                onToggleMonth={toggleMonth}
                perm={perm}
                rangeLabel={currentRangeLabel}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
